import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  TextInput,
  TextArea,
  Checkbox,
  CheckboxGroup,
  Dropdown,
} from "components/common";
import PageLayout from "components/layouts/PageLayout";
import Modal from "components/common/Modal";
import Client from "lib/api/Client";
import clientListCache from "lib/utils/clientListCache";
import { CLOUD_TYPE_KEYS, CLOUD_TYPE_LABELS, parseCloudType, parseCloudLogins } from "lib/utils/license";
import { UserContext } from "lib/context/UserContext";
import CloudUsersModal, { CLOUD_TABLE_STYLES, CLOUD_ICON_BTN, formatCloudUserDate } from "components/cloud-users/CloudUsersModal";
import { PencilIcon, TrashIcon, InformationCircleIcon } from "@heroicons/react/24/outline";

const CONTACT_TYPE_LABELS = {
  primary_poc: "Primary POC",
  billing_poc: "Billing POC",
  migration_poc: "Migration POC",
  migration_tech_poc: "Migration Tech POC",
};

const EVENT_TYPE_LABELS = {
  client_cloud_provisioning_added: "Cloud Provisioning Added to License",
  cloud_users_form_sent: "Intro Email / Cloud Users Form Sent",
  cloud_upgrade_email_sent: "Cloud Upgrade Email Sent",
  cloud_users_stored: "Cloud Users Stored",
  cloud_snow_ticket_created: "SNOW Ticket Created",
  cloud_users_zimcom_email_sent: "Cloud Users Zimcom Email Sent",
  cloud_credentials_sent: "Login Credentials Sent",
  cloud_license_onboarding_submitted: "License Submitted to Zimcom Onboarding API",
  cloud_license_onboarding_resubmitted: "License Manually Resubmitted to Zimcom Onboarding API",
};
// "Login Credentials Sent" can come from manual resend or either provider's
// batch endpoint — show whichever fired most recently.
const EVENT_TYPE_ALIASES = {
  cloud_credentials_sent: [
    "cloud_credentials_sent",
    "cloud_users_zimcom_credentials_sent",
    "cloud_users_deltek_cloud_credentials_sent",
  ],
};
// Resolves the event type among an alias group with the most recent
// occurred_at, or the plain eventType if it has no aliases.
function resolveLatestEventType(clientEvents, eventType) {
  const candidates = EVENT_TYPE_ALIASES[eventType] || [eventType];
  return candidates
    .filter((t) => clientEvents[t])
    .sort((a, b) => new Date(clientEvents[b]) - new Date(clientEvents[a]))[0];
}
// Event types only relevant to a specific cloud license type — hidden for
// clients whose license doesn't match.
const EVENT_TYPE_CLOUD_FILTER = {
  cloud_users_zimcom_email_sent: (cloudType) =>
    cloudType === "cldzd" || cloudType === "cldzs",
  cloud_snow_ticket_created: (cloudType) => cloudType === "clddeltek",
  cloud_license_onboarding_submitted: (cloudType) =>
    cloudType === "cldzd" || cloudType === "cldzs",
  cloud_license_onboarding_resubmitted: (cloudType) =>
    cloudType === "cldzd" || cloudType === "cldzs",
};

const EMPTY_CONTACT = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  contact_type: "primary_poc",
};

const CLOUD_EMAIL_TYPES = CLOUD_TYPE_KEYS;

function humanizeEventKey(key) {
  const s = String(key).replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Multi-line or long strings (e.g. license files with keys) are summarized, not shown.
function formatEventValue(value) {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  const s = String(value);
  if (s.length > 80 || /[\r\n]/.test(s)) return `(${s.length} characters, hidden)`;
  return s;
}

function InfoPopover({ id, openId, setOpenId, text }) {
  const open = openId === id;
  return (
    <span style={{ position: "relative", display: "inline-flex" }}>
      <button
        type="button"
        onClick={() => setOpenId(open ? null : id)}
        aria-label="More info"
        style={{ display: "inline-flex", alignSelf: "stretch", alignItems: "center", justifyContent: "center", background: "none", border: "none", padding: 0, margin: 0, lineHeight: 0, cursor: "pointer" }}
      >
        <InformationCircleIcon style={{ width: 18, height: 18, color: "#718096" }} />
      </button>
      {open && (
        <div style={{
          position: "absolute",
          top: "calc(100% + 6px)",
          left: 0,
          zIndex: 10,
          width: 280,
          background: "#fff",
          border: "1px solid #d1d5db",
          borderRadius: 6,
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          padding: "10px 12px",
          fontSize: "0.8125rem",
          color: "#374151",
        }}>
          {text}
        </div>
      )}
    </span>
  );
}

export default function ClientForm(props) {
  const { user } = useContext(UserContext);
  const isJohnWelch = user?.email === "johnwelch@deltek.com";
  const [uuid, setUuid] = useState("");
  const [name, setName] = useState("");  
  const [code, setCode] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [dealer, setDealer] = useState("ComputerEase");
  const [type, setType] = useState("");
  const [companies, setCompanies] = useState([]);
  const [expiration, setExpiration] = useState("2022-01-01");
  const [created_at, setCreatedAt] = useState("");
  const [updated_at, setUpdatedAt] = useState("");
  const [clientEvents, setClientEvents] = useState({});
  const [eventMetadata, setEventMetadata] = useState({});
  const [permissions, setPermissions] =  useState({
    api: false,
    apr: false,
    ar: false,
    avid: false,
    bd: false,
    betapbc: false,
    cd: false,
    cepr: false,
    cr: false,
    docpw: false,
    dp: false,
    ec: false,
    fepo: false,
    fewo: false,
    gl: false,
    hf20210709: false,
    ic: false,
    infin: false,
    mb: false,
    nvpay: false,
    odbc: false,
    palm: false,
    pm: false,
    pr: false,
    qtool: false,
    re: false,
    rw: false,
    sk: false,
    sq: false,
    sub: false,
    ta: false,
    tc: false,
    tracker: false,
    trade: false,
    web: false,
    wedl: false,
    wejc: false,
    wepr: false,
    wewo: false,
    wl: false
  });
  const [settings, setSettings] =  useState({});
  const [contacts, setContacts] = useState([]);
  const [newContact, setNewContact] = useState(EMPTY_CONTACT);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showContactsModal, setShowContactsModal] = useState(false);
  const [contactsSaving, setContactsSaving] = useState(false);
  const [contactsSaved, setContactsSaved] = useState(false);
  const [addContactSaving, setAddContactSaving] = useState(false);
  const [addContactError, setAddContactError] = useState("");
  const [notes, setNotes] =  useState("");
  const [enabled, setEnabled] = useState(true);
  const [syncDisabled, setSyncDisabled] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [newNotifications, setNewNotifications] = useState([]);
  const [newNotification, setNewNotification] = useState("");
  const [cloudUsers, setCloudUsers] = useState([]);
  const [showCloudUsersModal, setShowCloudUsersModal] = useState(false);
  const [cloudUsersLoading, setCloudUsersLoading] = useState(false);
  const [confirmDeleteContactIndex, setConfirmDeleteContactIndex] = useState(null);
  const [snowSyncing, setSnowSyncing] = useState(false);
  const [snowSyncResult, setSnowSyncResult] = useState(null);
  const [snowInfoLoading, setSnowInfoLoading] = useState(false);
  const [snowInfo, setSnowInfo] = useState(null);
  const [showSyncConfirmModal, setShowSyncConfirmModal] = useState(false);
  const [showNoDeltekIdModal, setShowNoDeltekIdModal] = useState(false);
  const [showNotAwsCloudModal, setShowNotAwsCloudModal] = useState(false);
  const [showServiceTicketModal, setShowServiceTicketModal] = useState(false);
  const [pendingCreateServiceTicket, setPendingCreateServiceTicket] = useState(false);
  const [showCloudEmailModal, setShowCloudEmailModal] = useState(false);
  const [cloudEmailSending, setCloudEmailSending] = useState(false);
  const [cloudEmailResult, setCloudEmailResult] = useState(null);
  const [openInfoPopoverId, setOpenInfoPopoverId] = useState(null);
  const [showCloudUsersFormModal, setShowCloudUsersFormModal] = useState(false);
  const [cloudUsersFormSending, setCloudUsersFormSending] = useState(false);
  const [cloudUsersFormResult, setCloudUsersFormResult] = useState(null);
  const [showSendCredentialsModal, setShowSendCredentialsModal] = useState(false);
  const [sendCredentialsSending, setSendCredentialsSending] = useState(false);
  const [sendCredentialsResult, setSendCredentialsResult] = useState(null);
  const [showSubmitLicenseModal, setShowSubmitLicenseModal] = useState(false);
  const [submitLicenseSending, setSubmitLicenseSending] = useState(false);
  const [submitLicenseResult, setSubmitLicenseResult] = useState(null);
  const [showPermissions, setShowPermissions] = useState(false);
  const [showCelicense, setShowCelicense] = useState(false);
  const [showEventLogs, setShowEventLogs] = useState(false);
  async function save() {
    if (props.match.params.uuid) {
      await Client.update(props.match.params.uuid, {
        name: name,
        code: code
      });
    } else {
      await Client.create({
        name: name,
        code: code
      });
    }
    props.history.push(`/clients`);
  }
  async function addNotification() {
    await setNotifications([...notifications, {uuid: Date.now(), message: newNotification}]);
    await setNewNotifications([...newNotifications, {message: newNotification}]);
    await setNewNotification("");
  }
  async function deleteNotification(uuid) {
    await setNotifications(notifications.filter((record)=>{return record.uuid != uuid}));
    await setNewNotifications(notifications.filter((record)=>{return record.uuid != uuid}));
  }
  async function load(uuid) {
    try{
      const response = await Client.load(uuid);
      const client = response.data;
      setName(client.name);
      setUuid(client.uuid);
      setCode(client.code);
      setApiKey(client.api_key);
      setPermissions(client.permissions || permissions);
      setSettings(client.settings);
      setContacts((client.settings?.contacts || []).map(({ is_primary, ...rest }) => rest));
      setType(client.type);
      setNotes(client.notes);
      setExpiration(client.expiration);
      setCompanies(client.companies);
      setUpdatedAt(client.updated_at);
      setCreatedAt(client.created_at);
      setClientEvents(client.events || {});
      setEventMetadata(client.eventMetadata || {});
      setSyncDisabled(client.sync_disabled);

      // Update the client in the list cache with latest data
      clientListCache.updateClient(client.uuid, client);

      loadCloudUsers(client.uuid);
      if (client.permissions?.celicense) {
        checkServiceNowRegistration(client.uuid);
      }
    }catch(exception){
      console.log("cant load");
      console.log(exception);
    }
  }
  async function toggleSync() {
    try {
      if (syncDisabled) {
        await Client.enableSync(uuid);
      } else {
        await Client.disableSync(uuid);
      }
      setSyncDisabled(!syncDisabled);
    } catch (e) {
      console.error(e);
    }
  }
  async function addContact() {
    const updatedContacts = [...contacts, { ...newContact }];
    setAddContactSaving(true);
    setAddContactError("");
    try {
      const payload = updatedContacts.map(({ is_primary, ...rest }) => rest);
      await Client.updateContacts(uuid, payload);
      setContacts(updatedContacts);
      setNewContact(EMPTY_CONTACT);
      setShowContactForm(false);
    } catch (e) {
      setAddContactError("Failed to add contact.");
    }
    setAddContactSaving(false);
  }

  async function removeContact(index) {
    const updatedContacts = contacts.filter((_, i) => i !== index);
    setConfirmDeleteContactIndex(null);
    try {
      const payload = updatedContacts.map(({ is_primary, ...rest }) => rest);
      await Client.updateContacts(uuid, payload);
      setContacts(updatedContacts);
    } catch (e) {
      console.error(e);
    }
  }

  async function saveContacts() {
    setContactsSaving(true);
    try {
      const payload = contacts.map(({ is_primary, ...rest }) => rest);
      await Client.updateContacts(uuid, payload);
      setContactsSaved(true);
      setTimeout(() => setContactsSaved(false), 3000);
    } catch (e) {
      console.error(e);
    }
    setContactsSaving(false);
  }

  async function loadCloudUsers(clientUuid) {
    const id = clientUuid || uuid;
    setCloudUsersLoading(true);
    try {
      const response = await Client.listCloudUsers(id);
      setCloudUsers(response.data || []);
    } catch (e) {
      console.error(e);
    }
    setCloudUsersLoading(false);
  }

  function syncToServiceNow() {
    if (parseCloudType(permissions?.celicense) !== "clddeltek") {
      setShowNotAwsCloudModal(true);
      return;
    }
    if (!permissions?.deltekid) {
      setShowNoDeltekIdModal(true);
      return;
    }
    setShowServiceTicketModal(true);
  }

  function answerServiceTicketQuestion(createServiceTicket) {
    setShowServiceTicketModal(false);
    const recordExists = snowInfo?.ok && snowInfo?.hasProductRecord;
    if (!recordExists) {
      setPendingCreateServiceTicket(createServiceTicket);
      setShowSyncConfirmModal(true);
      return;
    }
    doSyncToServiceNow(createServiceTicket);
  }

  function confirmSyncToServiceNow() {
    setShowSyncConfirmModal(false);
    doSyncToServiceNow(pendingCreateServiceTicket);
  }

  async function doSyncToServiceNow(createServiceTicket) {
    setSnowSyncing(true);
    setSnowSyncResult(null);
    try {
      const response = await Client.syncToServiceNow(uuid, createServiceTicket);
      if (response.status === 200) {
        setSnowSyncResult({ ok: true, message: "Synced to ServiceNow successfully." });
        checkServiceNowRegistration();
      } else {
        setSnowSyncResult({ ok: false, message: response.message || "Sync failed." });
      }
    } catch (e) {
      setSnowSyncResult({ ok: false, message: "An error occurred." });
    }
    setSnowSyncing(false);
  }

  async function checkServiceNowRegistration(overrideUuid) {
    const id = overrideUuid || uuid;
    if (!id) return;
    setSnowInfoLoading(true);
    setSnowInfo(null);
    try {
      const response = await Client.getServiceNowInfo(id);
      if (response.companyName) {
        setSnowInfo({ ok: true, ...response });
      } else {
        setSnowInfo({ ok: false, message: response.message || "Lookup failed." });
      }
    } catch (e) {
      setSnowInfo({ ok: false, message: "An error occurred." });
    }
    setSnowInfoLoading(false);
  }

  function getCloudEmailPreview() {
    const cloudType = parseCloudType(permissions?.celicense);
    if (!cloudType || !CLOUD_EMAIL_TYPES.includes(cloudType)) {
      return {
        canSend: false,
        warning: "This client does not have a cloud license (AWS Deltek Cloud or Zimcom Cloud) — no cloud upgrade email applies.",
      };
    }
    const primaryContacts = contacts.filter(
      (c) => c.contact_type === "primary_poc" && c.email
    );
    if (primaryContacts.length === 0) {
      return {
        canSend: false,
        warning: "No Primary POC contacts with email addresses found. Add a Primary POC contact before sending.",
      };
    }
    return {
      canSend: true,
      cloudType,
      cloudLabel: CLOUD_TYPE_LABELS[cloudType],
      recipients: primaryContacts,
      subject: "Welcome to ComputerEase Cloud — Your Migration Starts Here",
    };
  }

  async function sendCloudUpgradeEmail() {
    setCloudEmailSending(true);
    setCloudEmailResult(null);
    try {
      const response = await Client.sendCloudUpgradeEmail(uuid);
      if (response.status === 200 || response.status === 201) {
        setCloudEmailResult({ ok: true, message: "Cloud upgrade email sent successfully." });
        setClientEvents({ ...clientEvents, cloud_upgrade_email_sent: new Date().toISOString() });
      } else {
        setCloudEmailResult({ ok: false, message: response.message || "Failed to send email." });
      }
    } catch (e) {
      setCloudEmailResult({ ok: false, message: "An error occurred." });
    }
    setCloudEmailSending(false);
    setShowCloudEmailModal(false);
  }

  // Gate + recipient preview for the customer cloud users web form. Unlike the
  // cloud upgrade email, this applies to ANY cloud type (incl. Zimcom Shared).
  function getCloudUsersFormPreview() {
    const cloudType = parseCloudType(permissions?.celicense);
    if (!cloudType) {
      return {
        canSend: false,
        warning: "This client does not have a cloud license — the cloud users form does not apply.",
      };
    }
    const primaryContacts = contacts.filter(
      (c) => c.contact_type === "primary_poc" && c.email
    );
    if (primaryContacts.length === 0) {
      return {
        canSend: false,
        warning: "No Primary POC contacts with email addresses found. Add a Primary POC contact before sending.",
      };
    }
    return {
      canSend: true,
      cloudType,
      cloudLabel: CLOUD_TYPE_LABELS[cloudType] || cloudType,
      seatCount: parseCloudLogins(permissions?.celicense),
      recipients: primaryContacts,
    };
  }

  async function sendCloudUsersForm() {
    setCloudUsersFormSending(true);
    setCloudUsersFormResult(null);
    try {
      const response = await Client.sendCloudUsersForm(uuid);
      if (response.status === 200 || response.status === 201) {
        setCloudUsersFormResult({ ok: true, message: "Cloud users form link sent successfully." });
      } else {
        setCloudUsersFormResult({ ok: false, message: response.message || "Failed to send form link." });
      }
    } catch (e) {
      setCloudUsersFormResult({ ok: false, message: "An error occurred." });
    }
    setCloudUsersFormSending(false);
    setShowCloudUsersFormModal(false);
  }

  async function sendCloudCredentials() {
    setSendCredentialsSending(true);
    setSendCredentialsResult(null);
    try {
      const response = await Client.sendCloudCredentials(uuid);
      if (response.status === 200) {
        setSendCredentialsResult({ ok: true, message: response.message });
        setClientEvents({ ...clientEvents, cloud_credentials_sent: new Date().toISOString() });
        loadCloudUsers();
      } else {
        setSendCredentialsResult({ ok: false, message: response.message || "Failed to send credentials." });
      }
    } catch (e) {
      setSendCredentialsResult({ ok: false, message: "An error occurred." });
    }
    setSendCredentialsSending(false);
    setShowSendCredentialsModal(false);
  }

  async function submitLicenseToZimcom() {
    setSubmitLicenseSending(true);
    setSubmitLicenseResult(null);
    try {
      const response = await Client.submitLicenseToZimcom(uuid);
      if (response.status === 200) {
        setSubmitLicenseResult({ ok: true, message: response.message });
      } else {
        setSubmitLicenseResult({ ok: false, message: response.message || "Failed to submit license." });
      }
    } catch (e) {
      setSubmitLicenseResult({ ok: false, message: "An error occurred." });
    }
    setSubmitLicenseSending(false);
    setShowSubmitLicenseModal(false);
  }


  async function remove() {
    await Client.delete(uuid);
    props.history.push(`/clients`);
  }
  useEffect(() => {
    if (props.match.params.uuid) {
      load(props.match.params.uuid);
    }
  }, []);
  const deleteBtn = props.match.params.uuid ? (
    <Button className="button button--danger" onClick={remove}>
      Delete
    </Button>
  ) : (
    ""
  );
  const CompanyList = !companies ? "" : companies.map((company) => {
    return (
      <div className="row" key={company.uuid}>
        <div className="col-xs-12 col-sm-6">

        <Link to={`/company/${company.uuid}/`}>
                          {company.name}
                        </Link>
        </div>
      </div>
    )
  })

  // const companiesLink = props.match.params.uuid ? (
  //   <Link to={`/client/${props.match.params.uuid}/companies`}>Companies</Link>
  // ) : (
  //   ""
  // );
  const permissionCheckboxes = Object.keys(permissions).map((module)=>{
    return (
      <Checkbox
      key={module}
      title={module}
      checked={permissions[module]}
      onChange={e =>
        setPermissions({
          ...permissions,
          ...{[module]: !permissions[module]}
        })
      }
    />
    )
  })


  const NotificationRows =  notifications.map((record)=>{
    return (
      <tr key={record.uuid}>
        <td>{record.message}</td>
        <td><Button className="button button--danger" onClick={(e)=>{deleteNotification(record.uuid)}}>Delete</Button></td>
      </tr>
    )
  });

  const card = {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: "20px 24px",
    marginBottom: 20,
  };

  const cardTitle = {
    fontSize: 13,
    fontWeight: 700,
    color: "#374151",
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    marginBottom: 16,
    paddingBottom: 10,
    borderBottom: "1px solid #f3f4f6",
  };

  const fieldLabel = {
    fontSize: 11,
    fontWeight: 600,
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginBottom: 4,
  };

  const fieldValue = {
    fontSize: 14,
    color: "#111827",
  };

  function Field({ label, children, mono = false, col = "col-xs-12 col-sm-3" }) {
    return (
      <div className={col} style={{ marginBottom: 16 }}>
        <div style={fieldLabel}>{label}</div>
        <div style={{ ...fieldValue, fontFamily: mono ? "monospace" : undefined, fontSize: mono ? "0.88em" : undefined }}>
          {children || "-"}
        </div>
      </div>
    );
  }

  const cloudLoginLimit = parseCloudLogins(permissions?.celicense);
  const cloudType = parseCloudType(permissions?.celicense);
  const isZimcom = cloudType === "cldzd" || cloudType === "cldzs";
  const hasNoLicense = !permissions?.celicense;

  return (
    <PageLayout>
      <h1>Client Details</h1>
      <div className="hr hr--darkest"></div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16, alignItems: "center" }}>
        <a className="button button--plain" href="#" onClick={props.history.goBack}>Back</a>
        {uuid && (
          <Button onClick={() => {
            setOpenInfoPopoverId(null);
            setShowContactsModal(true);
          }}>
            Manage {contacts.length} Contact{contacts.length !== 1 ? "s" : ""}
          </Button>
        )}
        {uuid && (
          <Button onClick={() => { setOpenInfoPopoverId(null); setShowCloudUsersModal(true); loadCloudUsers(); }}>
            {cloudUsersLoading ? "Loading..." : `Manage ${cloudUsers.length} Cloud User${cloudUsers.length !== 1 ? "s" : ""}`}
          </Button>
        )}
        {uuid && (
          <span style={{ display: "inline-flex", alignItems: "stretch", gap: "6px" }}>
            <Button onClick={() => { setOpenInfoPopoverId(null); setCloudEmailResult(null); setShowCloudEmailModal(true); }}>
              Send Customer Migration Email
            </Button>
            <InfoPopover
              id="cloudUpgradeEmail"
              openId={openInfoPopoverId}
              setOpenId={setOpenInfoPopoverId}
              text="Sends the standard cloud onboarding email with migration steps. Only applies to existing customers moving to AWS Deltek Cloud or Zimcom Cloud, and is sent to the client's Primary POC contacts — add one before sending if none exist."
            />
          </span>
        )}
        {cloudEmailResult && (
          <span style={{ color: cloudEmailResult.ok ? "#38a169" : "#e53e3e", fontSize: "0.875rem" }}>
            {cloudEmailResult.message}
          </span>
        )}
        {uuid && (
          <span style={{ display: "inline-flex", alignItems: "stretch", gap: "6px" }}>
            <Button onClick={() => { setOpenInfoPopoverId(null); setCloudUsersFormResult(null); setShowCloudUsersFormModal(true); }}>
              Send New Customer Email
            </Button>
            <InfoPopover
              id="cloudUsersForm"
              openId={openInfoPopoverId}
              setOpenId={setOpenInfoPopoverId}
              text="Sends an introductory email with a link to the cloud users form. Applies to any cloud license type (including Zimcom Shared Cloud), and is sent to the client's Primary POC contacts — add one before sending if none exist."
            />
          </span>
        )}
        {cloudUsersFormResult && (
          <span style={{ color: cloudUsersFormResult.ok ? "#38a169" : "#e53e3e", fontSize: "0.875rem" }}>
            {cloudUsersFormResult.message}
          </span>
        )}
        {uuid && isJohnWelch && (
          <span style={{ display: "inline-flex", alignItems: "stretch", gap: "6px" }}>
            <Button onClick={() => { setOpenInfoPopoverId(null); setSendCredentialsResult(null); setShowSendCredentialsModal(true); }}>
              Send Login Credentials
            </Button>
            <InfoPopover
              id="sendCredentials"
              openId={openInfoPopoverId}
              setOpenId={setOpenInfoPopoverId}
              text="Resends login credentials to every active cloud user for this client, using the same username/password/login URL rules as the automatic LCO/AWS credential emails. Users pending provisioning, without a password on file, or without a login URL on file are skipped. Only visible to johnwelch@deltek.com while this is being tested."
            />
          </span>
        )}
        {sendCredentialsResult && (
          <span style={{ color: sendCredentialsResult.ok ? "#38a169" : "#e53e3e", fontSize: "0.875rem" }}>
            {sendCredentialsResult.message}
          </span>
        )}
        {uuid && isZimcom && isJohnWelch && (
          <span style={{ display: "inline-flex", alignItems: "stretch", gap: "6px" }}>
            <Button onClick={() => { setOpenInfoPopoverId(null); setSubmitLicenseResult(null); setShowSubmitLicenseModal(true); }}>
              Resubmit License to Zimcom
            </Button>
            <InfoPopover
              id="submitLicenseToZimcom"
              openId={openInfoPopoverId}
              setOpenId={setOpenInfoPopoverId}
              text="Manually (re-)submits this client's license.dat to the Zimcom/LCO partner onboarding API. Only visible to johnwelch@deltek.com while this is being tested."
            />
          </span>
        )}
        {submitLicenseResult && (
          <span style={{ color: submitLicenseResult.ok ? "#38a169" : "#e53e3e", fontSize: "0.875rem" }}>
            {submitLicenseResult.message}
          </span>
        )}
      </div>

      {/* Client Information */}
      <div style={card}>
        <div style={cardTitle}>Client Information</div>
        <div className="row">
          <Field label="Client Name" col="col-xs-12 col-sm-4">{name}</Field>
          <Field label="License #" col="col-xs-12 col-sm-2">{code}</Field>
          <Field label="License Type" col="col-xs-12 col-sm-2">{type || "-"}</Field>
          <Field label="Dealer" col="col-xs-12 col-sm-2">{dealer}</Field>
          <Field label="Expiration" col="col-xs-12 col-sm-2">{expiration}</Field>
        </div>
        <div className="row">
          <Field label="Cloud Type" col="col-xs-12 col-sm-2">{cloudType ? CLOUD_TYPE_LABELS[cloudType] : "-"}</Field>
          <Field label="Cloud Users" col="col-xs-12 col-sm-2">
            {cloudLoginLimit != null ? `${cloudUsers.length} / ${cloudLoginLimit}` : cloudUsers.length > 0 ? cloudUsers.length : "-"}
          </Field>
          <Field label="Created At" col="col-xs-12 col-sm-3">
            {created_at ? new Date(created_at).toLocaleString() : "-"}
          </Field>
          <Field label="Updated At" col="col-xs-12 col-sm-3">
            {updated_at ? new Date(updated_at).toLocaleString() : "-"}
          </Field>
        </div>
        {notes && (
          <div className="row">
            <Field label="Notes (Internal)" col="col-xs-12">{notes}</Field>
          </div>
        )}
      </div>

      {/* Status / Event Dates */}
      <div style={card}>
        <div style={cardTitle}>Status / Event Dates</div>
        <div className="row">
          {Object.entries(EVENT_TYPE_LABELS)
            .filter(([eventType]) => {
              const filter = EVENT_TYPE_CLOUD_FILTER[eventType];
              return !filter || filter(cloudType);
            })
            .map(([eventType, label]) => {
              const resolvedType = resolveLatestEventType(clientEvents, eventType);
              return (
                <Field key={eventType} label={label} col="col-xs-12 col-sm-3">
                  {resolvedType ? (
                    <>
                      {formatCloudUserDate(clientEvents[resolvedType])}
                      {eventMetadata[resolvedType]?.request_number && (
                        <div style={{ fontSize: "0.8em", color: "#718096" }}>
                          Ticket: {eventMetadata[resolvedType].request_number}
                        </div>
                      )}
                    </>
                  ) : (
                    "-"
                  )}
                </Field>
              );
            })}
        </div>
      </div>

      {/* ServiceNow */}
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", ...cardTitle }}>
          <span>ServiceNow</span>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {!isZimcom && !hasNoLicense && (
              <button
                onClick={syncToServiceNow}
                disabled={snowSyncing || !uuid}
                style={{ fontSize: 12, padding: "3px 12px", borderRadius: 5, border: "1px solid #d1d5db", background: "#fff", color: "#374151", cursor: "pointer" }}
              >
                {snowSyncing ? "Syncing..." : "Sync to ServiceNow"}
              </button>
            )}
            {snowSyncResult && (
              <span style={{ color: snowSyncResult.ok ? "#38a169" : "#e53e3e", fontSize: 12 }}>
                {snowSyncResult.message}
              </span>
            )}
            <button
              onClick={() => checkServiceNowRegistration()}
              disabled={snowInfoLoading || !uuid || isZimcom || hasNoLicense}
              style={{
                fontSize: 12, padding: "3px 12px", borderRadius: 5, border: "1px solid #d1d5db",
                background: isZimcom || hasNoLicense ? "#f3f4f6" : "#fff",
                color: isZimcom || hasNoLicense ? "#9ca3af" : "#374151",
                cursor: isZimcom || hasNoLicense ? "not-allowed" : "pointer",
              }}
            >
              <span style={{ fontSize: 15, display: "inline-block", transform: snowInfoLoading ? "rotate(360deg)" : "none" }}>↻</span>
            </button>
          </div>
        </div>
        {snowInfoLoading && <p style={{ color: "#9ca3af", margin: 0 }}>Loading...</p>}
        {!snowInfoLoading && snowInfo && !snowInfo.ok && (
          <p style={{ color: "#e53e3e", margin: 0 }}>{snowInfo.message}</p>
        )}
        {!snowInfoLoading && snowInfo?.ok && (
          isZimcom ? (
            <div className="row">
              <Field label="Company" col="col-xs-12 col-sm-6">
                {snowInfo.companyName}
                {permissions?.deltekid ? ` [${permissions.deltekid}]` : ""}
              </Field>
            </div>
          ) : (
          <>
            <div className="row">
              <Field label="Company" col="col-xs-12 col-sm-4">{snowInfo.companyName}</Field>
              {snowInfo.hasProductRecord ? (
                <>
                  <Field label="Service" col="col-xs-12 col-sm-4">{snowInfo.service}</Field>
                  <Field label="Status" col="col-xs-12 col-sm-4">{snowInfo.status}</Field>
                </>
              ) : (
                <Field label="Product Record" col="col-xs-12 col-sm-4">
                  <span style={{ color: "#e53e3e" }}>Not found</span>
                </Field>
              )}
            </div>
            {snowInfo.hasProductRecord && (
              <>
                <div className="row">
                  <Field label="Live In" col="col-xs-12 col-sm-3">{snowInfo.liveInSystem}</Field>
                  <Field label="Go-Live Date" col="col-xs-12 col-sm-3">{snowInfo.goLiveDate}</Field>
                  <Field label="Deployed Users" col="col-xs-12 col-sm-2">{snowInfo.deployedLicenseCount ?? "-"}</Field>
                  <Field label="Sold Users" col="col-xs-12 col-sm-2">{snowInfo.soldUsers ?? "-"}</Field>
                  <Field label="Primary SaaS Admin" col="col-xs-12 col-sm-4">{snowInfo.primarySaasAdmin}</Field>
                </div>
                <div className="row">
                  <Field label="Pod" col="col-xs-12 col-sm-3">{snowInfo.podId?.display_value ?? snowInfo.podId}</Field>
                  <Field label="Cluster" col="col-xs-12 col-sm-3">{snowInfo.cluster}</Field>
                  <Field label="License Attached" col="col-xs-12 col-sm-2">
                    {snowInfo.licenseAttached ? (
                      <span style={{ color: "#059669", fontWeight: 600 }}>Yes</span>
                    ) : (
                      <span style={{ color: "#dc2626", fontWeight: 600 }}>No</span>
                    )}
                  </Field>
                  {snowInfo.licenseAttached && (
                    <Field label="License File" col="col-xs-12 col-sm-4">
                      {snowInfo.licenseFileName || "-"}
                      {snowInfo.licenseUploadedOn && (
                        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                          Uploaded {snowInfo.licenseUploadedOn}
                        </div>
                      )}
                    </Field>
                  )}
                </div>
                <div className="row">
                  <Field label="Cloud Users Attached" col="col-xs-12 col-sm-2">
                    {snowInfo.cloudUsersAttached ? (
                      <span style={{ color: "#059669", fontWeight: 600 }}>Yes</span>
                    ) : (
                      <span style={{ color: "#dc2626", fontWeight: 600 }}>No</span>
                    )}
                  </Field>
                  {snowInfo.cloudUsersAttached && (
                    <Field label="Cloud Users File" col="col-xs-12 col-sm-4">
                      {snowInfo.cloudUsersFileName || "-"}
                      {snowInfo.cloudUsersUploadedOn && (
                        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                          Uploaded {snowInfo.cloudUsersUploadedOn}
                        </div>
                      )}
                    </Field>
                  )}
                </div>
              </>
            )}
          </>
          )
        )}
        {!snowInfoLoading && !snowInfo && !hasNoLicense && (
          <p style={{ color: "#9ca3af", margin: 0, fontSize: 13 }}>No data — loading on page open, or click ↻ to refresh.</p>
        )}
        {isZimcom && (
          <p style={{ color: "#9ca3af", margin: "8px 0 0", fontSize: 12 }}>
            We don't sync Zimcom-licensed customers to ServiceNow.
          </p>
        )}
        {hasNoLicense && (
          <p style={{ color: "#9ca3af", margin: "8px 0 0", fontSize: 12 }}>
            This client has no license set in their record, so ServiceNow sync is unavailable.
          </p>
        )}
      </div>
      <Modal show={showNotAwsCloudModal}>
        <div style={{ padding: "24px", width: "100%" }}>
          <h2 style={{ margin: "0 0 16px 0" }}>Sync to ServiceNow</h2>
          <p style={{ marginBottom: "20px" }}>
            This client's cloud type is {cloudType ? CLOUD_TYPE_LABELS[cloudType] : "not set"}. Sync to
            ServiceNow is only available for clients licensed for AWS Deltek Cloud.
          </p>
          <div className="button-group">
            <Button className="button button--plain" onClick={() => setShowNotAwsCloudModal(false)}>
              Close
            </Button>
          </div>
        </div>
      </Modal>
      <Modal show={showNoDeltekIdModal}>
        <div style={{ padding: "24px", width: "100%" }}>
          <h2 style={{ margin: "0 0 16px 0" }}>Sync to ServiceNow</h2>
          <p style={{ marginBottom: "20px" }}>
            No Deltek ID has been set for this customer, unable to communicate with ServiceNow.
            Please have the license updated providing the Deltek ID.
          </p>
          <div className="button-group">
            <Button className="button button--plain" onClick={() => setShowNoDeltekIdModal(false)}>
              Close
            </Button>
          </div>
        </div>
      </Modal>
      <Modal show={showServiceTicketModal}>
        <div style={{ padding: "24px", width: "100%" }}>
          <h2 style={{ margin: "0 0 16px 0" }}>Sync to ServiceNow</h2>
          <p style={{ marginBottom: "20px" }}>
            Should a service ticket be created for this sync?
          </p>
          <div className="button-group">
            <Button className="button" onClick={() => answerServiceTicketQuestion(true)}>
              Yes
            </Button>
            <Button className="button button--plain" onClick={() => answerServiceTicketQuestion(false)}>
              No
            </Button>
          </div>
        </div>
      </Modal>
      <Modal show={showSyncConfirmModal}>
        <div style={{ padding: "24px", width: "100%" }}>
          <h2 style={{ margin: "0 0 16px 0" }}>Sync to ServiceNow</h2>
          <p style={{ marginBottom: "16px" }}>
            A company record for this client was not found in ServiceNow. Syncing will create one,
            send the license.dat.txt file, and send the cloud_users.json file. Creating the record
            will also kick off an automated process that starts provisioning their cloud environment.
          </p>
          {cloudUsers.length === 0 && (
            <p style={{ color: "#c05621", background: "#fffaf0", border: "1px solid #fbd38d", borderRadius: "4px", padding: "12px", marginBottom: "20px" }}>
              Warning: This client has 0 cloud users. Syncing may create an exception in cloud provisioning.
            </p>
          )}
          <div className="button-group">
            <Button className="button" onClick={confirmSyncToServiceNow}>
              Continue
            </Button>
            <Button className="button button--plain" onClick={() => setShowSyncConfirmModal(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
      <Modal show={showSubmitLicenseModal}>
        <div style={{ padding: "24px", width: "100%" }}>
          <h2 style={{ margin: "0 0 16px 0" }}>Resubmit License to Zimcom</h2>
          <p style={{ marginBottom: "16px" }}>
            This will POST this client's license.dat to the Zimcom/LCO partner onboarding API, which
            triggers real downstream provisioning on their end. Only do this for a legitimate resend/test —
            Zimcom will process it as a real onboarding submission.
          </p>
          <p style={{ color: "#c05621", background: "#fffaf0", border: "1px solid #fbd38d", borderRadius: "4px", padding: "12px", marginBottom: "20px" }}>
            Warning: this is not a dry run. Confirming sends the license to Zimcom's live onboarding endpoint.
          </p>
          <div className="button-group">
            <Button className="button" onClick={submitLicenseToZimcom} disabled={submitLicenseSending}>
              {submitLicenseSending ? "Submitting..." : "Continue"}
            </Button>
            <Button className="button button--plain" onClick={() => setShowSubmitLicenseModal(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
      <CloudUsersModal
        show={showCloudUsersModal}
        onClose={() => setShowCloudUsersModal(false)}
        clientUuid={uuid}
        clientName={name}
        cloudType={cloudType}
        cloudLoginLimit={cloudLoginLimit}
        cloudUsers={cloudUsers}
        setCloudUsers={setCloudUsers}
        loading={cloudUsersLoading}
        onReload={loadCloudUsers}
        onCredentialsSent={() => setClientEvents((prev) => ({ ...prev, cloud_credentials_sent: new Date().toISOString() }))}
      />
      <Modal show={showContactsModal} contentStyle={{ padding: 0 }}>
        <div style={{ padding: "24px", width: "100%" }}>
          <h2 style={{ margin: "0 0 16px 0" }}>Contacts</h2>
          {contacts.length > 0 && (
            <table style={CLOUD_TABLE_STYLES.table}>
              <thead>
                <tr>
                  <th style={CLOUD_TABLE_STYLES.th}>Contact</th>
                  <th style={CLOUD_TABLE_STYLES.th}>Phone</th>
                  <th style={CLOUD_TABLE_STYLES.th}>Type</th>
                  <th style={{ ...CLOUD_TABLE_STYLES.th, textAlign: "right" }}></th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((c, i) => (
                  <tr key={i}>
                    <td style={CLOUD_TABLE_STYLES.td}>
                      <div>{c.first_name} {c.last_name}</div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>{c.email}</div>
                    </td>
                    <td style={CLOUD_TABLE_STYLES.td}>{c.phone || "—"}</td>
                    <td style={CLOUD_TABLE_STYLES.td}>{CONTACT_TYPE_LABELS[c.contact_type] || c.contact_type}</td>
                    <td style={{ ...CLOUD_TABLE_STYLES.td, textAlign: "right" }}>
                      <button type="button" title="Remove" style={CLOUD_ICON_BTN} onClick={() => setConfirmDeleteContactIndex(i)}>
                        <TrashIcon style={{ width: 16, height: 16, color: "#dc2626" }} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {contacts.length === 0 && !showContactForm && (
            <p style={{ color: "#718096", marginBottom: "12px" }}>No contacts on file.</p>
          )}
          {showContactForm && (
            <div style={{ background: "#f7fafc", border: "1px solid #e2e8f0", borderRadius: "4px", padding: "16px", marginTop: "8px" }}>
              <h3 style={{ marginTop: 0 }}>New Contact</h3>
              <div className="row">
                <div className="col-xs-12 col-sm-6">
                  <TextInput
                    label="First Name"
                    type="text"
                    value={newContact.first_name}
                    onChange={e => setNewContact({ ...newContact, first_name: e.target.value })}
                  />
                </div>
                <div className="col-xs-12 col-sm-6">
                  <TextInput
                    label="Last Name"
                    type="text"
                    value={newContact.last_name}
                    onChange={e => setNewContact({ ...newContact, last_name: e.target.value })}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12 col-sm-6">
                  <TextInput
                    label="Email"
                    type="email"
                    value={newContact.email}
                    onChange={e => setNewContact({ ...newContact, email: e.target.value })}
                  />
                </div>
                <div className="col-xs-12 col-sm-6">
                  <TextInput
                    label="Phone (optional)"
                    type="text"
                    value={newContact.phone}
                    onChange={e => setNewContact({ ...newContact, phone: e.target.value })}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12 col-sm-6">
                  <Dropdown
                    label="Contact Type"
                    value={newContact.contact_type}
                    onChange={e => setNewContact({ ...newContact, contact_type: e.target.value })}
                  >
                    <option value="primary_poc">Primary POC</option>
                    <option value="billing_poc">Billing POC</option>
                    <option value="migration_poc">Migration POC</option>
                    <option value="migration_tech_poc">Migration Tech POC</option>
                  </Dropdown>
                </div>
              </div>
              {addContactError && (
                <p style={{ color: "#e53e3e", marginTop: "12px" }}>{addContactError}</p>
              )}
              <div className="button-group" style={{ marginTop: "12px" }}>
                <Button
                  className="button"
                  onClick={addContact}
                  disabled={!newContact.first_name || !newContact.last_name || !newContact.email || addContactSaving}
                >
                  {addContactSaving ? "Adding..." : "Add"}
                </Button>
                <Button className="button button--plain" onClick={() => { setShowContactForm(false); setNewContact(EMPTY_CONTACT); setAddContactError(""); }}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginTop: "20px", paddingTop: "16px", borderTop: "1px solid #f3f4f6" }}>
            {!showContactForm && (
              <Button className="button button--plain" onClick={() => setShowContactForm(true)}>+ Add Contact</Button>
            )}
            <Button className="button" onClick={saveContacts} disabled={contactsSaving}>
              {contactsSaving ? "Saving..." : "Save Contacts"}
            </Button>
            <Button className="button button--plain" onClick={() => {
              setShowContactsModal(false);
              setShowContactForm(false);
              setNewContact(EMPTY_CONTACT);
            }}>
              Close
            </Button>
            {contactsSaved && <span style={{ color: "#38a169" }}>Saved!</span>}
          </div>
        </div>
      </Modal>
      <Modal show={confirmDeleteContactIndex !== null}>
        <div style={{ padding: "24px", width: "100%" }}>
          <h2 style={{ margin: "0 0 16px 0" }}>Remove Contact</h2>
          <p style={{ marginBottom: "20px" }}>
            Are you sure you want to remove {confirmDeleteContactIndex !== null && contacts[confirmDeleteContactIndex]
              ? `${contacts[confirmDeleteContactIndex].first_name} ${contacts[confirmDeleteContactIndex].last_name}`
              : "this contact"}? This cannot be undone.
          </p>
          <div className="button-group">
            <Button className="button button--danger" onClick={() => removeContact(confirmDeleteContactIndex)}>
              Remove
            </Button>
            <Button className="button button--plain" onClick={() => setConfirmDeleteContactIndex(null)}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
      <Modal show={showCloudEmailModal}>
        {(() => {
          const preview = getCloudEmailPreview();
          return (
            <div style={{ padding: "24px", width: "100%" }}>
              <h2 style={{ margin: "0 0 16px 0" }}>Send Customer Migration Email</h2>
              <p style={{ marginBottom: "8px" }}>
                <strong>Client:</strong> {name}
              </p>
              {preview.canSend ? (
                <>
                  {clientEvents.cloud_upgrade_email_sent && (
                    <p style={{ color: "#c05621", background: "#fffaf0", border: "1px solid #fbd38d", borderRadius: "4px", padding: "12px", marginBottom: "20px" }}>
                      Note: This email was already sent on {formatCloudUserDate(clientEvents.cloud_upgrade_email_sent)}.
                    </p>
                  )}
                  <p style={{ marginBottom: "8px" }}>
                    <strong>Cloud Type:</strong> {preview.cloudLabel}
                  </p>
                  <p style={{ marginBottom: "8px" }}>
                    <strong>Subject:</strong> {preview.subject}
                  </p>
                  <p style={{ marginBottom: "8px", fontWeight: "600" }}>Recipients (Primary POC contacts):</p>
                  <ul style={{ margin: "0 0 20px 0", paddingLeft: "20px" }}>
                    {preview.recipients.map((c, i) => (
                      <li key={i} style={{ marginBottom: "4px" }}>
                        {c.first_name} {c.last_name} — {c.email}
                      </li>
                    ))}
                  </ul>
                  <p style={{ color: "#718096", fontSize: "0.875rem", marginBottom: "20px" }}>
                    This will send the standard cloud onboarding email with migration steps tailored for {preview.cloudLabel}.
                  </p>
                  <div className="button-group">
                    <Button className="button" onClick={sendCloudUpgradeEmail} disabled={cloudEmailSending}>
                      {cloudEmailSending ? "Sending..." : "Confirm & Send"}
                    </Button>
                    <Button className="button button--plain" onClick={() => setShowCloudEmailModal(false)} disabled={cloudEmailSending}>
                      Cancel
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p style={{ color: "#c05621", background: "#fffaf0", border: "1px solid #fbd38d", borderRadius: "4px", padding: "12px", marginBottom: "20px" }}>
                    {preview.warning}
                  </p>
                  <div className="button-group">
                    <Button className="button button--plain" onClick={() => setShowCloudEmailModal(false)}>
                      Close
                    </Button>
                  </div>
                </>
              )}
            </div>
          );
        })()}
      </Modal>
      <Modal show={showCloudUsersFormModal}>
        {(() => {
          const preview = getCloudUsersFormPreview();
          return (
            <div style={{ padding: "24px", width: "100%" }}>
              <h2 style={{ margin: "0 0 16px 0" }}>Send New Customer Email</h2>
              <p style={{ marginBottom: "8px" }}>
                <strong>Client:</strong> {name}
              </p>
              {preview.canSend ? (
                <>
                  {clientEvents.cloud_users_form_sent && (
                    <p style={{ color: "#c05621", background: "#fffaf0", border: "1px solid #fbd38d", borderRadius: "4px", padding: "12px", marginBottom: "20px" }}>
                      Note: This email was already sent on {formatCloudUserDate(clientEvents.cloud_users_form_sent)}.
                    </p>
                  )}
                  <p style={{ marginBottom: "8px" }}>
                    <strong>Cloud Type:</strong> {preview.cloudLabel}
                  </p>
                  <p style={{ marginBottom: "8px" }}>
                    <strong>Licensed Cloud Seats:</strong>{" "}
                    {preview.seatCount != null ? preview.seatCount : "—"}
                  </p>
                  <p style={{ marginBottom: "8px", fontWeight: "600" }}>Recipients (Primary POC contacts):</p>
                  <ul style={{ margin: "0 0 20px 0", paddingLeft: "20px" }}>
                    {preview.recipients.map((c, i) => (
                      <li key={i} style={{ marginBottom: "4px" }}>
                        {c.first_name} {c.last_name} — {c.email}
                      </li>
                    ))}
                  </ul>
                  <p style={{ color: "#718096", fontSize: "0.875rem", marginBottom: "20px" }}>
                    This emails each Primary POC a secure, single-use link (valid 7 days) to a form
                    where they provide a cloud user for each licensed seat.
                  </p>
                  <div className="button-group">
                    <Button className="button" onClick={sendCloudUsersForm} disabled={cloudUsersFormSending}>
                      {cloudUsersFormSending ? "Sending..." : "Confirm & Send"}
                    </Button>
                    <Button className="button button--plain" onClick={() => setShowCloudUsersFormModal(false)} disabled={cloudUsersFormSending}>
                      Cancel
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p style={{ color: "#c05621", background: "#fffaf0", border: "1px solid #fbd38d", borderRadius: "4px", padding: "12px", marginBottom: "20px" }}>
                    {preview.warning}
                  </p>
                  <div className="button-group">
                    <Button className="button button--plain" onClick={() => setShowCloudUsersFormModal(false)}>
                      Close
                    </Button>
                  </div>
                </>
              )}
            </div>
          );
        })()}
      </Modal>
      <Modal show={showSendCredentialsModal}>
        <div style={{ padding: "24px", width: "100%" }}>
          <h2 style={{ margin: "0 0 16px 0" }}>Send Login Credentials</h2>
          <p style={{ marginBottom: "8px" }}>
            <strong>Client:</strong> {name}
          </p>
          {resolveLatestEventType(clientEvents, "cloud_credentials_sent") && (
            <p style={{ color: "#c05621", background: "#fffaf0", border: "1px solid #fbd38d", borderRadius: "4px", padding: "12px", marginBottom: "20px" }}>
              Note: Login credentials were last sent on{" "}
              {formatCloudUserDate(
                clientEvents[resolveLatestEventType(clientEvents, "cloud_credentials_sent")]
              )}.
            </p>
          )}
          <p style={{ color: "#718096", fontSize: "0.875rem", marginBottom: "20px" }}>
            This will resend login credentials to every active cloud user for this client, using their
            username/password/login URL already on file. Users pending provisioning, without a password
            on file, or without a login URL on file will be skipped.
          </p>
          <div className="button-group">
            <Button className="button" onClick={sendCloudCredentials} disabled={sendCredentialsSending}>
              {sendCredentialsSending ? "Sending..." : "Confirm & Send"}
            </Button>
            <Button className="button button--plain" onClick={() => setShowSendCredentialsModal(false)} disabled={sendCredentialsSending}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
      <div style={card}>
        <div style={cardTitle}>API Information</div>
        <div style={{ display: "flex", gap: 24 }}>
          <Field label="API Enabled" col="">
            <span style={{ fontWeight: 600, color: enabled ? "#059669" : "#dc2626" }}>{enabled ? "Yes" : "No"}</span>
          </Field>
          <Field label="Sync Disabled" col="">
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontWeight: 600, color: syncDisabled ? "#dc2626" : "#059669" }}>{syncDisabled ? "Yes" : "No"}</span>
              <button
                onClick={toggleSync}
                disabled={!uuid}
                style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, border: "1px solid #d1d5db", background: "#f9fafb", color: "#374151", cursor: uuid ? "pointer" : "not-allowed" }}
              >
                {syncDisabled ? "Enable Sync" : "Disable Sync"}
              </button>
            </div>
          </Field>
        </div>
      </div>
      <div style={card}>
        <button
          onClick={() => setShowEventLogs(v => !v)}
          style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: 0, background: "none", border: "none", cursor: "pointer", ...cardTitle, marginBottom: showEventLogs ? 16 : 0 }}
        >
          <span>Event Logs</span>
          <span style={{ fontSize: 16, color: "#9ca3af" }}>{showEventLogs ? "▲" : "▼"}</span>
        </button>
        {showEventLogs && (Object.keys(clientEvents).length === 0 ? (
          <span style={{ color: "#9ca3af", fontSize: 13 }}>No events recorded.</span>
        ) : (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {Object.entries(clientEvents)
              .sort((x, y) => (new Date(y[1]).getTime() || 0) - (new Date(x[1]).getTime() || 0))
              .map(([type, date], i) => {
                const meta = eventMetadata[type] || {};
                const metaEntries = Object.entries(meta);
                return (
                  <div
                    key={type}
                    style={{ display: "grid", gridTemplateColumns: "260px 150px 1fr", gap: 16, padding: "12px 0", borderTop: i === 0 ? "none" : "1px solid #f3f4f6", alignItems: "start" }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>{humanizeEventKey(type)}</div>
                    <div style={{ fontSize: 13, color: "#6b7280", whiteSpace: "nowrap" }}>{formatCloudUserDate(date)}</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 8px" }}>
                      {metaEntries.length === 0 ? (
                        <span style={{ color: "#9ca3af", fontSize: 13 }}>—</span>
                      ) : (
                        metaEntries.map(([k, v]) => (
                          <span key={k} style={{ fontSize: 12, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 4, padding: "2px 8px", wordBreak: "break-all" }}>
                            <span style={{ color: "#6b7280" }}>{humanizeEventKey(k)}:</span>{" "}
                            <span style={{ color: "#111827" }}>{formatEventValue(v)}</span>
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        ))}
      </div>
      <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, marginBottom: 12, background: "#fff" }}>
        <button
          onClick={() => setShowPermissions(v => !v)}
          style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 18px", background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.07em" }}
        >
          <span>Permissions</span>
          <span style={{ fontSize: 16, color: "#9ca3af" }}>{showPermissions ? "▲" : "▼"}</span>
        </button>
        {showPermissions && (
          <div style={{ padding: "0 18px 16px" }}>
            <pre style={{ margin: 0, maxHeight: 300, overflowY: "auto", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, padding: "10px 14px", fontSize: 12, fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
              {JSON.stringify(permissions, null, 2)}
            </pre>
          </div>
        )}
      </div>
      {permissions?.celicense && (
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, marginBottom: 12, background: "#fff" }}>
          <button
            onClick={() => setShowCelicense(v => !v)}
            style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 18px", background: "none", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.07em" }}
          >
            <span>ComputerEase License File</span>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span
                title="Download license.dat"
                onClick={e => {
                  e.stopPropagation();
                  const content = permissions.celicense.replace(/\r\n|\n|\r/g, "\r\n");
                  const blob = new Blob([content], { type: "text/plain" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "license.dat";
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", border: "1px solid #d1d5db", borderRadius: 4, color: "#374151", background: "#f9fafb", letterSpacing: "0.03em", whiteSpace: "nowrap" }}
              >
                Download license.dat
              </span>
              <span style={{ fontSize: 16, color: "#9ca3af" }}>{showCelicense ? "▲" : "▼"}</span>
            </div>
          </button>
          {showCelicense && (
            <div style={{ padding: "0 18px 16px" }}>
              <pre style={{ margin: 0, maxHeight: 300, overflowY: "auto", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, padding: "10px 14px", fontSize: 12, fontFamily: "monospace", whiteSpace: "pre-wrap" }}>
                {permissions.celicense.replace(/\r\n/g, "\n")}
              </pre>
            </div>
          )}
        </div>
      )}
      <div className="hr hr--darkest m-top-20 m-bot-20"></div>
      <div className="row">
      <div className="col-xs-12 col-sm-6">
          <label><span className="label__span">Companies</span></label>
          {CompanyList}
        </div>
      </div>
      <div className="hr hr--darkest m-top-20 m-bot-20"></div>
      <div className="row m-bot-100">
        <div className="col-xs-6">
          <div className="button-group">
            {/* <Button className="button" onClick={save}>
              Save
            </Button> */}
            <a className="button button--plain" href="#" onClick={props.history.goBack}>
              Cancel
            </a>
          </div>
        </div>
        <div className="col-xs-6 end-xs">
          {/* <div className="button-group">
            {deleteBtn}
          </div> */}
        </div>
      </div>
    </PageLayout>
  );
}
